import { CanAccess } from '@modules/common/components/CanAccess';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  useGetRequestDashboardAccessLink,
  useIsDashboardEditable,
} from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import { Button, Menu } from '@mui/material';
import {
  ArtefactType,
  PermissionOperation,
  PermissionResource,
  useAskForArtefactAccessMutation,
} from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { RequestSentContent } from './RequestSentContent';

export const RequestDashboardAccess = () => {
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRequestSent, setShowRequestSent] = useState(false);

  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const approvalURL = useGetRequestDashboardAccessLink({
    dashboardLink: window.location.toString(),
  });

  const [askForArtefactAccess, { loading, error }] =
    useAskForArtefactAccessMutation({
      variables: {
        request: {
          artefactID: dashboard?.id ?? '',
          tenantID,
          artefactType: ArtefactType.Dashboard,
          approvalURL,
        },
      },
    });

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (!error) {
      return;
    }

    showErrorNotification({
      contentComponent: 'Failed to send the edit request.',
      props: {},
    });
  }, [error, showErrorNotification]);

  const isEditable = useIsDashboardEditable();

  useEffect(() => {
    if (!isEditable) {
      setShowRequestSent(false);
    }
  }, [isEditable]);

  if (!dashboard || isEditable || !dashboard.id) {
    return null;
  }

  const open = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  if (showRequestSent) {
    return (
      <>
        <Button
          variant="contained"
          ref={setAnchorEl}
          startIcon={<DoneIcon />}
          onClick={openMenu}
        >
          Request Sent
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <RequestSentContent onClose={onClose} dashboard={dashboard} />
        </Menu>
      </>
    );
  }

  const onRequestAccess = async () => {
    await askForArtefactAccess();
    setShowRequestSent(true);
  };

  return (
    <CanAccess
      resource={PermissionResource.Artefact}
      operation={PermissionOperation.Write}
    >
      <LoadingButton
        loading={loading}
        variant="contained"
        onClick={onRequestAccess}
        sx={{
          minWidth: 'max-content',
        }}
      >
        Request Access
      </LoadingButton>
    </CanAccess>
  );
};
